/**
 * Lineage graph for the Learn poster — the prerequisite edges drawn between
 * lesson tiles, and the ancestor set a hovered/focused tile lights up.
 *
 * Pure and token-free. Everything is restricted to the *visible* node set
 * passed in, so an edge never points at a node the poster isn't showing
 * (e.g. a guitar prerequisite, which lives outside the ear-training grid).
 */

import type { CurriculumNode } from "@music-app/learning-system";

export interface LineageEdge {
  /** Prerequisite node id (the edge's source). */
  from: string;
  /** Dependent node id (the edge's target). */
  to: string;
  /** Track of the `from` node — the poster colors each edge by its source. */
  fromTrack: string;
}

/** node id → its prerequisite ids, filtered to the visible set. */
export function buildAdjacency(nodes: CurriculumNode[]): Map<string, string[]> {
  const visible = new Set(nodes.map((n) => n.id));
  const adjacency = new Map<string, string[]>();
  for (const node of nodes) {
    adjacency.set(
      node.id,
      node.prerequisites.filter((p) => visible.has(p)),
    );
  }
  return adjacency;
}

/**
 * Transitive prerequisites of `id` (not including `id` itself). Cycle-guarded
 * via a visited set, so a malformed graph can't loop forever.
 */
export function ancestorsOf(
  id: string,
  adjacency: Map<string, string[]>,
): Set<string> {
  const seen = new Set<string>();
  const stack = [...(adjacency.get(id) ?? [])];
  while (stack.length > 0) {
    const current = stack.pop()!;
    if (seen.has(current)) continue;
    seen.add(current);
    for (const prereq of adjacency.get(current) ?? []) {
      if (!seen.has(prereq)) stack.push(prereq);
    }
  }
  return seen;
}

/** Every prerequisite edge with both endpoints in the visible set. */
export function buildEdges(nodes: CurriculumNode[]): LineageEdge[] {
  const visible = new Set(nodes.map((n) => n.id));
  const trackOf = new Map(nodes.map((n) => [n.id, n.track]));
  const edges: LineageEdge[] = [];
  for (const node of nodes) {
    for (const prereq of node.prerequisites) {
      if (visible.has(prereq)) {
        edges.push({ from: prereq, to: node.id, fromTrack: trackOf.get(prereq)! });
      }
    }
  }
  return edges;
}
